import { Clear as ClearIcon } from '@mui/icons-material';
import {
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Theme,
	Typography,
} from '@mui/material';
import { useMutation } from '@parkchirp/api-client';
import { uniqBy } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';

import { CREATE_SEQUENCING_TIER } from '../../../mutations';
import { SetShowSnackbar } from '../../../types';
import { SequencingCountAndRate, SequencingTier } from '../../../types/leases';
import { EditorModal } from '../../modals';

type AddSequencingTierProps = {
	subscriptionId: string;
	countsAndRates: SequencingCountAndRate[];
	tiers: SequencingTier[];
	setNumber: number;
	theme: Theme;
	setShowSnackbar: SetShowSnackbar;
	handleClose: () => void;
	pollSequencingTiers: () => Promise<void>;
};

export const AddSequencingTierModal: FC<AddSequencingTierProps> = ({
	subscriptionId,
	countsAndRates,
	tiers,
	setNumber,
	theme,
	setShowSnackbar,
	handleClose,
	pollSequencingTiers,
}) => {
	const [tierNumber, setTierNumber] = useState<number>(null);
	const [rateId, setRateId] = useState<string>(null);
	const [sublotId, setSublotId] = useState<number>(null);
	const [selectedCountAndRate, setSelectedCountAndRate] = useState<SequencingCountAndRate>(null);
	const [createSequencingTier, { loading: createInProgress }] = useMutation(CREATE_SEQUENCING_TIER);

	useEffect(() => {
		if (!rateId || !sublotId) {
			setSelectedCountAndRate(null);
			return;
		}

		const selection = countsAndRates.find((car) => car.rateId === rateId && car.sublotId === sublotId);
		setSelectedCountAndRate(selection);
	}, [rateId, sublotId, countsAndRates, setSelectedCountAndRate]);

	const rateOptions = useCallback(() => {
		if (!countsAndRates) {
			return [];
		}

		const options = sublotId ? countsAndRates.filter((car) => car.sublotId === sublotId) : countsAndRates;

		const mappedOptions = options.map(({ rateId: id, rateName }) => ({
			rateId: id,
			rateName,
		}));

		const rates = uniqBy(mappedOptions, 'rateId');

		return rates;
	}, [sublotId, countsAndRates]);

	const sublotOptions = useCallback(() => {
		if (!countsAndRates) {
			return [];
		}

		const options = rateId ? countsAndRates.filter((car) => car.rateId === rateId) : countsAndRates;

		const mappedOptions = options.map(({ sublotId: id, sublotName }) => ({
			sublotId: id,
			sublotName,
		}));

		const sublots = uniqBy(mappedOptions, 'sublotId');

		return sublots;
	}, [rateId, countsAndRates]);

	const validateTier = useCallback(() => {
		const invalidFields: string[] = [];

		if (!rateId) {
			invalidFields.push('Rate');
		}

		if (!sublotId) {
			invalidFields.push('Sublot');
		}

		if (!tierNumber) {
			invalidFields.push('Tier Number');
		}

		return invalidFields;
	}, [rateId, sublotId, tierNumber]);

	const saveSequencingTier = useCallback(async () => {
		try {
			const invalidFields = validateTier();

			if (invalidFields.length) {
				setShowSnackbar({
					message: `Required Field(s): ${invalidFields.join(', ')}`,
					severity: 'error',
					autoHideDuration: null,
				});

				return;
			}

			const {
				rateId: selectedRateId,
				sublotId: selectedSublotId,
				leasingCountAndRateId: selectedLeasingCountAndRateId,
				leasingConversionRightId: selectedleasingConversionRightId,
			} = selectedCountAndRate;

			await createSequencingTier({
				variables: {
					subscriptionId,
					rateId: selectedRateId,
					sublotId: selectedSublotId,
					leasingCountAndRateId: selectedLeasingCountAndRateId,
					leasingConversionRightId: selectedleasingConversionRightId,
					tierNumber,
					setNumber,
				},
				context: { apiVersion: 'v2' },
				onCompleted: async () => {
					setShowSnackbar({
						message: 'Tier successfully added',
						severity: 'success',
						autoHideDuration: 3000,
					});
					handleClose();
					await pollSequencingTiers();
				},
			});
		} catch {
			setShowSnackbar({
				message: 'Failed to add sequencing tier',
				severity: 'error',
				autoHideDuration: 5000,
			});
		}
	}, [
		validateTier,
		selectedCountAndRate,
		createSequencingTier,
		subscriptionId,
		tierNumber,
		setNumber,
		setShowSnackbar,
		handleClose,
		pollSequencingTiers,
	]);

	const handleSave = useCallback(async () => {
		await saveSequencingTier();
	}, [saveSequencingTier]);

	// Generate the list of available tier numbers by calculating the length of the existing tiers array + 1.
	// Available tier numbers:
	// No existing tiers: [1]
	// One existing tier: [1, 2]
	// Five existing tiers: [1, 2, 3, 4, 5, 6]
	const tierNumberOptions = [...Array(tiers.length + 1).keys()].map((x) => x + 1);

	return (
		<EditorModal
			handleClose={handleClose}
			handleSave={handleSave}
			headerText='Add Sequencing Tier'
			headerBorder
			loading={createInProgress}
			saveDisabled={!selectedCountAndRate || !tierNumber}
			theme={theme}
		>
			<Grid
				container
				spacing={2}
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignSelf: 'center',
					mt: 2,
					mb: 2,
				}}
			>
				<Grid item xs={12} mb={2}>
					<Typography variant='body1'>
						Select a rate/sublot combination and the desired tier number.
					</Typography>
				</Grid>
				<Grid item xs={4}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel id='rate-label'>Rate</InputLabel>
						<Select
							fullWidth
							labelId='rate-select-label'
							id='rate-select'
							value={rateId || ''}
							label='Rate'
							disabled={createInProgress}
							onChange={(e) => setRateId(e.target.value)}
							endAdornment={
								rateId && (
									<InputAdornment sx={{ position: 'absolute', right: 16 }} position='end'>
										<IconButton
											onClick={() => {
												setRateId(null);
											}}
										>
											<ClearIcon />
										</IconButton>
									</InputAdornment>
								)
							}
						>
							{rateOptions().map(({ rateId: key, rateName }) => (
								<MenuItem key={key} value={key}>
									{rateName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={4}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel id='sublot-label'>Sublot</InputLabel>
						<Select
							fullWidth
							labelId='sublot-select-label'
							id='sublot-select'
							value={sublotId}
							label='Sublot'
							disabled={createInProgress}
							onChange={(e) => setSublotId(Number(e.target.value))}
							endAdornment={
								sublotId && (
									<InputAdornment sx={{ position: 'absolute', right: 16 }} position='end'>
										<IconButton
											onClick={() => {
												setSublotId(null);
											}}
										>
											<ClearIcon />
										</IconButton>
									</InputAdornment>
								)
							}
						>
							{sublotOptions().map(({ sublotId: key, sublotName }) => (
								<MenuItem key={key} value={key}>
									{sublotName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={4}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel id='tier-number-label'>Tier Number</InputLabel>
						<Select
							fullWidth
							labelId='tier-number-select-label'
							id='tier-number-select'
							value={tierNumber}
							label='Tier Number'
							disabled={createInProgress}
							onChange={(e) => setTierNumber(Number(e.target.value))}
							endAdornment={
								tierNumber && (
									<InputAdornment sx={{ position: 'absolute', right: 16 }} position='end'>
										<IconButton
											onClick={() => {
												setTierNumber(null);
											}}
										>
											<ClearIcon />
										</IconButton>
									</InputAdornment>
								)
							}
						>
							{tierNumberOptions.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
		</EditorModal>
	);
};
